// Single source of truth for subscription pricing.
//
// The euro amounts here are DISPLAY values — what actually gets charged is
// defined by the Stripe Price objects referenced in STRIPE_PRICE_IDS below.
// When changing a price: create the new Price in Stripe (prices are
// immutable), then update the amount and the id here — one file, one commit.
//
// Pure data, no server deps — safe to import from client components.

/**
 * How much a plan's yearly price saves against paying monthly, rounded.
 *
 * Was a single constant, which was fine while Solo was the only sellable plan.
 * It is not: Solo saves 26% and Plus 20%, so one number shown above both plan
 * cards necessarily overstates one of them. Derived per plan instead, so it
 * cannot drift from the prices it describes.
 */
export function yearlyDiscountPct(plan: {
  monthly: number;
  yearlyPerMonth: number;
}): number {
  return Math.round(((plan.monthly - plan.yearlyPerMonth) / plan.monthly) * 100);
}

/** Every plan we model. Only `available` ones can be subscribed to. */
export type PlanKey = "solo" | "plus" | "pro" | "premium";

/** Plans that map to a real Stripe Price and can actually be billed. */
export type BillablePlan = "solo" | "plus";

/**
 * Stripe Price ids per billable plan. Test (sandbox) and live mode each have
 * their own Price objects; the code picks by the loaded API key's mode.
 *
 * Prices are immutable in Stripe: to change a price, create a new Price and
 * swap the id here. `priceIdFor` throws on a TODO placeholder, so an unset id
 * fails loudly at the point of use rather than silently billing the wrong plan.
 */
export const STRIPE_PRICE_IDS: Record<
  BillablePlan,
  Record<"monthly" | "yearly", { test: string; live: string }>
> = {
  solo: {
    monthly: {
      test: "price_1TuuyZECapw4U8Yn6Laz2U1z",
      live: "price_1TvBRgE9AE2yIo6tughCezDk",
    },
    yearly: {
      test: "price_1TuuyZECapw4U8Ynr9hJXg2v",
      live: "price_1TvBReE9AE2yIo6t6xuMtX3o",
    },
  },
  plus: {
    monthly: {
      test: "price_1UCPXLECapw4U8Yn5fjFTEoo",
      live: "price_1UCPbsE9AE2yIo6tWSEmc9UH",
    },
    yearly: {
      test: "price_1UCPXLECapw4U8YnLJcLMs3K",
      live: "price_1UCPbsE9AE2yIo6txJV5BL5p",
    },
  },
};

/**
 * How many bookable providers each plan allows. Enforced when adding staff, and
 * checked in reverse before a downgrade.
 *
 * Only `solo: 1` is a product boundary — it is the reason to buy Plus. The rest
 * are sanity guards set well above any real clinic: capping Plus lower would
 * block a growing customer with nowhere to upgrade to, since Pro is not yet
 * sellable. Revisit when Pro launches and its differentiators are settled.
 */
export const PLAN_PROVIDER_LIMITS: Record<PlanKey, number> = {
  solo: 1,
  plus: 25,
  pro: 25,
  premium: 100,
};

/** 23% SK DPH, inclusive — attached to every subscription so invoices itemize VAT. */
export const STRIPE_TAX_RATE_IDS = {
  test: "txr_1Tuuz6ECapw4U8YnmBhqhJQw",
  live: "txr_1TvC4qE9AE2yIo6tnJW7K5vm",
} as const;

/**
 * Seller legal identifiers printed in the footer of every subscription invoice
 * (a SK VAT invoice must carry the seller's IČO/DIČ/IČ DPH). Set on the
 * subscription's invoice_settings.footer at creation — avoids depending on a
 * Stripe account-default invoice template (which can't be set per-mode in code
 * and can't be tested in sandbox). The buyer's IČO/DIČ come from the customer
 * object (see details route), separately.
 */
export const SELLER_INVOICE_FOOTER =
  "Waveward s.r.o. · IČO: 54786673 · DIČ: 2121783235 · IČ DPH: SK2121783235";

export const PLANS = [
  {
    key: "solo",
    name: "Solo",
    description: "Pre jedného poskytovateľa a malú ambulanciu.",
    monthly: 39,
    yearlyPerMonth: 29,
    yearlyTotal: 348,
    features: [
      "1 poskytovateľ, jedna lokalita",
      "Profilová stránka ambulancie",
      "Blokovanie času a dovolenky",
      "Automatické email potvrdenia a pripomienky",
    ],
    available: true,
  },
  {
    key: "plus",
    name: "Plus",
    description: "Pre ambulancie s viacerými poskytovateľmi a personálom.",
    monthly: 49,
    yearlyPerMonth: 39,
    yearlyTotal: 468,
    features: [
      "Všetko zo Solo",
      "Viac poskytovateľov, jedna lokalita",
      "Role používateľov (správca, poskytovateľ, recepcia)",
      "Pozvánky pre personál e-mailom",
    ],
    available: true,
  },
  {
    key: "pro",
    name: "Pro",
    description: "Pre ambulancie s viacerými poskytovateľmi.",
    monthly: 69,
    yearlyPerMonth: 55,
    yearlyTotal: 660,
    features: [
      "Všetko zo Solo",
      "Viac poskytovateľov, jedna lokalita",
      "Role používateľov",
      "Čakacia listina a digitálna čakáreň",
    ],
    available: false,
  },
  {
    key: "premium",
    name: "Premium",
    description: "Pre polikliniky a viac pracovísk.",
    monthly: 129,
    yearlyPerMonth: 109,
    yearlyTotal: 1308,
    features: [
      "Všetko z Pro",
      "White-label branding (logo, farby, vlastná doména)",
      "Hromadné správy pacientom",
      "Rezervácie pre rodiny a opakujúce sa termíny",
    ],
    available: false,
  },
];
